import { OfferQuotationDetailMaintenanceUnit } from '@/features/offerCostBudgets/constants/offerCostBudget.constant'
import { Button, Input, Select, Text } from '@chakra-ui/react'
import { createColumnHelper } from '@tanstack/react-table'
import CostBudget from './CostBudget'
import {
  CostBudgetType,
  OfferQuotationDetailInstallAndSupplyUnit,
  getQuotationDetailTableColumnIds,
  OfferQuotationTemplateScopeOfWork,
} from '@eapproval/offer-shared'
import { DeleteButton } from '@/components/Elements/Button'
import { InputCell, NumberInputCell, PriceCell, RawTextCell, SelectCell } from '@/components/Table/components/Cell'
import { camelToWords } from '@/utils/stringHelper'
import { NumberInput } from '@/components/Form'
import { useMemo } from 'react'
import DatePickerCell from '@/components/Table/components/Cell/DatePickerCell'
const columnHelper = createColumnHelper()

export const columnIdToDetail = ({ id, ...rest }) => {
  const canEdit = rest?.canEdit ?? true
  const extraDisable = !canEdit

  switch (id) {
    case 'action':
      return columnHelper.display({
        id: 'action',
        size: 50,
        enableHiding: false,
        enableColumnFilter: false,
        cell: ({ getValue, row: { original, index }, table, extraDisable = false }) => {
          const value = getValue()
          const isDisabled = table.options.meta?.disabled || extraDisable

          if (isDisabled) {
            return <></>
          }

          return <DeleteButton onClick={() => rest.handleRemove(index)} />
        },
      })
    case 'scopeOfWorkSelect':
      return columnHelper.accessor('scopeOfWork', {
        size: 300,
        cell: (cell) => (
          <SelectCell
            {...cell}
            extraDisable={extraDisable}
            options={Object.values(OfferQuotationTemplateScopeOfWork).map((val) => ({
              value: val,
              label: camelToWords(val),
            }))}
          />
        ),
      })

    case 'modelNumber':
      return columnHelper.accessor('modelNumber', {
        header: 'Model No.',
        cell: (cell) => <InputCell {...cell} extraDisable={extraDisable} />,
      })
    case 'productDescription':
      return columnHelper.accessor('productDescription', {
        header: 'Description',
        cell: (cell) => <InputCell {...cell} extraDisable={extraDisable} />,
      })
    case 'quantity':
      return columnHelper.accessor('quantity', {
        cell: (cell) => <NumberInputCell {...cell} extraDisable={extraDisable} decimalScale={2} />,
      })
    case 'unit':
      return columnHelper.accessor('unit', {
        header: 'Unit',
        cell: (cell) => <InputCell {...cell} extraDisable={extraDisable} />,
      })
    case 'unitSelect':
      return columnHelper.accessor('unit', {
        header: 'Unit',
        cell: (cell) => (
          <SelectCell
            {...cell}
            extraDisable={extraDisable}
            options={Object.values(OfferQuotationDetailInstallAndSupplyUnit).map((val) => ({
              value: val,
              label: val,
            }))}
          />
        ),
      })
    case 'unitMaintenance':
      return columnHelper.accessor('unit', {
        header: 'Unit',
        cell: (cell) => (
          <SelectCell
            {...cell}
            extraDisable={extraDisable}
            options={Object.values(OfferQuotationDetailMaintenanceUnit).map((val) => ({
              value: val,
              label: val,
            }))}
          />
        ),
      })
    case 'unitRate':
      return columnHelper.accessor('unitRate', {
        header: () => <span>Unit Rate {rest?.currency ? `(${rest.currency})` : ''}</span>,
        cell: (cell) => (
          // <NumberInputCell {...cell} extraDisable={extraDisable} decimalScale={2} pricePrefix={rest?.currency} />
          <NumberInputCell {...cell} extraDisable={extraDisable} decimalScale={2} />
        ),
      })
    case 'discount':
      return columnHelper.accessor('discount', {
        header: 'Discount %',
        cell: (cell) => <NumberInputCell {...cell} extraDisable={extraDisable} decimalScale={2} />,
      })
    case 'totalPrice':
      return columnHelper.accessor('totalPrice', {
        header: () => <span>Sub-total {rest?.currency ? `(${rest.currency})` : ''}</span>,
        // cell: (cell) => <PriceCell {...cell} currency={rest?.currency} />,
        cell: (cell) => <PriceCell {...cell} />,
      })
    case 'country':
      return columnHelper.accessor('country', {
        header: 'Country / Territory of Origin',
        cell: (cell) => <InputCell {...cell} extraDisable={extraDisable} />,
      })
    case 'AL':
      return columnHelper.accessor('AL', {
        cell: (cell) => <InputCell {...cell} extraDisable={extraDisable} />,
      })
    case 'ECCN':
      return columnHelper.accessor('ECCN', {
        cell: (cell) => <InputCell {...cell} extraDisable={extraDisable} />,
      })
    case 'HSCode':
      return columnHelper.accessor('HSCode', {
        cell: (cell) => <InputCell {...cell} extraDisable={extraDisable} />,
      })
    case 'ECCNDetail':
      return columnHelper.accessor('ECCNDetail', {
        header: () => <span>ECCN Checklist</span>,
        size: 100,
        cell: ({ row: { index, original } }) => {
          if (canEdit && rest.handleOpenECCNDetailModel && (original.ECCN || original.ECCN === '')) {
            return (
              <Button onClick={() => rest.handleOpenECCNDetailModel(original.ECCNDetail ?? [])}>
                View ECCN Checklist
              </Button>
            )
          }
          return <></>
        },
      })

    case 'startDate':
      return columnHelper.accessor('startDate', {
        header: 'Start Date',
        cell: (cell) => <DatePickerCell {...cell} extraDisable={extraDisable} />,
      })
    case 'endDate':
      return columnHelper.accessor('endDate', {
        header: 'End Date',
        cell: (cell) => <DatePickerCell {...cell} extraDisable={extraDisable} />,
      })
    case 'siteLocation':
      return columnHelper.accessor('siteLocation', {
        header: 'Site Locations / Areas / Building',
        cell: (cell) => <InputCell {...cell} extraDisable={extraDisable} />,
      })
    case 'numberOfWorker':
      return columnHelper.accessor('numberOfWorker', {
        header: 'No. of worker on site',
        cell: (cell) => <InputCell {...cell} extraDisable={extraDisable} />,
      })
    case 'system':
      return columnHelper.accessor('system', {
        cell: (cell) => <InputCell {...cell} extraDisable={extraDisable} />,
      })
    case 'category':
      return columnHelper.accessor('category', {
        header: () => <span>Category</span>,
        size: 200,
        cell: (cell) => {
          const isSupplySiteWorkCategoryDisabled =
            rest?.costBudgetType === CostBudgetType.SUPPLY_SITE_WORK &&
            cell.row.original?.scopeOfWork &&
            cell.row.original.scopeOfWork !== OfferQuotationTemplateScopeOfWork.SUPPLY

          return (
            <SelectCell
              {...cell}
              options={rest?.categoryOptions ?? []}
              extraDisable={extraDisable || rest?.disableCategory || isSupplySiteWorkCategoryDisabled}
            />
          )
        },
      })
    case 'SAPMaterialNumber':
      return columnHelper.accessor('SAPMaterialNumber', {
        header: 'SAP Material Number',
        cell: RawTextCell,
      })
    default:
      return columnHelper.accessor(id, {
        cell: (cell) => <InputCell {...cell} extraDisable={extraDisable} />,
      })
  }
}

export const getQuotationTemplateDetailColumns = ({
  costBudgetType,
  isTemplate = false,
  canRemove = true,
  editableColumnIds,
  ...rest
}) => {
  let columnIds = getQuotationDetailTableColumnIds({ costBudgetType, isTemplate: isTemplate, canRemove: canRemove })

  return columnIds.map((id) =>
    columnIdToDetail({
      id,
      costBudgetType,
      canEdit: Array.isArray(editableColumnIds) ? editableColumnIds.includes(id) : true,
      ...rest,
    })
  )
}
